import React, { useState } from 'react';
import { Check, Copy, BookOpen, Quote } from 'lucide-react';

// This component can be used as a standalone section or within an aside
const HowToCite = ({ isAsideTip = false }) => {
    const [copiedCitation, setCopiedCitation] = useState(false);
    const [copiedDoi, setCopiedDoi] = useState(false);

    const citation = `Sorokina, O., Dominic, D., Bayés, À. et al. SynaptopathyDB integrates synaptic proteomes, genetic and phenotypic data to advance research on nervous system disorders. Sci Rep 15, 42986 (2025). https://doi.org/10.1038/s41598-025-26969-z`;

    const doi = "10.1038/s41598-025-26969-z";

    const handleCopyCitation = () => {
        try {
            // Check if the Clipboard API is available
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(citation)
                    .then(() => {
                        setCopiedCitation(true);
                        setTimeout(() => setCopiedCitation(false), 2000);
                    })
                    .catch(err => {
                        console.error('Failed to copy text: ', err);
                        fallbackCopyTextToClipboard(citation);
                    });
            } else {
                // Fallback for browsers that don't support the Clipboard API
                fallbackCopyTextToClipboard(citation);
            }
        } catch (err) {
            console.error('Copy to clipboard failed: ', err);
        }
    };

    const handleCopyDoi = () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(doi)
                    .then(() => {
                        setCopiedDoi(true);
                        setTimeout(() => setCopiedDoi(false), 2000);
                    })
                    .catch(err => {
                        console.error('Failed to copy text: ', err);
                        fallbackCopyTextToClipboard(doi);
                    });
            } else {
                fallbackCopyTextToClipboard(doi);
            }
        } catch (err) {
            console.error('Copy to clipboard failed: ', err);
        }
    };

    // Fallback copy method using document.execCommand
    const fallbackCopyTextToClipboard = (text) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            
            // Make the textarea out of viewport
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);

            // Select and copy
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            
            if (successful) {
                if (text === citation) {
                    setCopiedCitation(true);
                    setTimeout(() => setCopiedCitation(false), 2000);
                } else {
                    setCopiedDoi(true);
                    setTimeout(() => setCopiedDoi(false), 2000);
                }
            }
        } catch (err) {
            console.error('Fallback copy failed: ', err);
        }
    };

    // When used in the aside as a tip, use a more compact layout
    if (isAsideTip) {
        return (
            <div className="bg-blue-50 p-3 rounded-md border border-blue-200 mb-3">
                <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                    <Quote className="h-4 w-4 text-blue-600 mr-1" />
                    How to Cite
                </h3>
                
                <div className="text-xs text-blue-700 mb-2">
                    {citation}
                </div>
                
                <div className="flex justify-between items-center">
                    <div className="flex items-center text-xs">
                        <span className="text-blue-700 mr-1">DOI:</span>
                        <a 
                            href={`https://doi.org/${doi}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                        >
                            {doi}
                        </a>
                    </div>
                    
                    <button 
                        onClick={handleCopyCitation}
                        className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                    >
                        {copiedCitation ? (
                            <>
                                <Check className="h-3 w-3 mr-1" />
                                Copied
                            </>
                        ) : (
                            <>
                                <Copy className="h-3 w-3 mr-1" />
                                Copy
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    // The full version for the main content area
    return (
        <div className="bg-white p-6 shadow rounded-lg" id="how-to-cite">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <Quote className="h-6 w-6 text-blue-600 mr-2" />
                How to Cite our work
            </h2>
            
            <p className="text-gray-700 mb-4">
                If you use SynaptopathyDB in your research, please cite our paper:
            </p>
            
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                        <BookOpen className="h-5 w-5 text-blue-600 mr-2" />
                        <h3 className="font-medium text-gray-900">Citation</h3>
                    </div>
                    <button 
                        onClick={handleCopyCitation}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        {copiedCitation ? (
                            <>
                                <Check className="h-4 w-4 mr-1" />
                                Copied
                            </>
                        ) : (
                            <>
                                <Copy className="h-4 w-4 mr-1" />
                                Copy Citation
                            </>
                        )}
                    </button>
                </div>
                <p className="text-gray-700 text-sm">
                    {citation}
                </p>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-6">
                <div className="flex items-center">
                    <span className="text-gray-700 mr-2">DOI:</span>
                    <a 
                        href={`https://doi.org/${doi}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                    >
                        {doi}
                    </a>
                </div>
                
                <button 
                    onClick={handleCopyDoi}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                    {copiedDoi ? (
                        <>
                            <Check className="h-4 w-4 mr-1" />
                            Copied
                        </>
                    ) : (
                        <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy DOI
                        </>
                    )}
                </button>
            </div>
            
            {/* <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Additional References</h4>
                <p className="text-blue-700 text-sm">
                    For the underlying Synaptome.db database, please also cite:
                </p>
                <p className="text-blue-700 text-sm mt-2">
                    Sorokina, O., Sorokin, A., Armstrong, J.D. (2022). Synaptome.db: a bioconductor package for synaptic proteomics data. Bioinformatics Advances, 2(1), vbac095. https://doi.org/10.1093/bioadv/vbac095
                </p>
            </div> */}
        </div>
    );
};

export default HowToCite;